import type { QueryFunctionContext } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useGlobals } from 'hooks/useGlobals';
import type { CountryCode } from 'services/shopify';
import { ShopifyService } from 'services/shopify';

export const PRODUCT_ENABLED_COUNTRIES_KEY = 'productEnabledCountries';
type ProductEnabledCountriesQueryKey = [
  typeof PRODUCT_ENABLED_COUNTRIES_KEY,
  string | undefined,
  string[],
];

/* INFO: productByHandle when fetched with inContext(country: countryCode)
 * will return null if the product is not available in the country. */
export const fetchProductEnabledCountries = async ({
  queryKey,
}: QueryFunctionContext<ProductEnabledCountriesQueryKey>) => {
  const [, productId, countryCodes] = queryKey;

  if (!productId) {
    return [];
  }

  const response = await Promise.all(
    countryCodes.map(async (countryCode) => {
      const { product } = await ShopifyService.getProductById({
        id: productId,
        country: countryCode as CountryCode,
      });

      return { countryCode, isEnabled: product !== null };
    }),
  );

  const enabledCountries = response.reduce<string[]>(
    (acc, { countryCode, isEnabled }) =>
      isEnabled ? [...acc, countryCode] : acc,
    [],
  );

  return enabledCountries;
};

export const useProductEnabledCountries = (productId?: string) => {
  const { data: globals } = useGlobals();

  const countryCodes = globals.availableCountries.map(
    (availableCountry) => availableCountry.isoCode,
  );

  return useQuery({
    queryKey: [PRODUCT_ENABLED_COUNTRIES_KEY, productId, countryCodes],
    enabled: !!productId,
    queryFn: fetchProductEnabledCountries,
  });
};
